import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * FigureChip — a number a report's text does not type (E14-design §5;
 * CLAUDE.md rule 7). Report prose carries `{{token}}` names where a figure
 * goes; the report's token table answers each one, and the reading form
 * shows the answer in this chip so a reader sees which words are figures
 * from code rather than prose.
 *
 *   - `value` is the token table's formatted string, printed as given;
 *   - `value` null: nothing answers the token. It prints an em dash in the
 *     critical tone, as the PDF prints a dash.
 *
 * Same tokens as the KPI source pill (paper-100, ink-100 hairline), at
 * text height so it sits inside a sentence. Load-bearing primitive
 * (CLAUDE.md rule 3): the report editor and every report reading form use
 * this one chip.
 */
export interface FigureChipProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** The token's name, without braces: `vendor.1.index`. */
  name: string;
  value: string | null;
  /** Where the figure comes from, for the hover text. */
  source?: string;
}

export const FigureChip = React.forwardRef<HTMLSpanElement, FigureChipProps>(
  ({ name, value, source = 'Computed by code', className, ...props }, ref) => (
    <span
      ref={ref}
      data-token={name}
      title={value === null ? `Nothing in this report answers {{${name}}}` : source}
      className={cn(
        'inline rounded border px-1 py-px font-medium [box-decoration-break:clone]',
        value === null
          ? 'border-posture-critical bg-posture-critical-tint text-posture-critical'
          : 'border-ink-100 bg-paper-100 text-ink-900',
        className,
      )}
      {...props}
    >
      {value ?? '—'}
      {value === null ? <span className="sr-only">{` (no value for ${name})`}</span> : null}
    </span>
  ),
);
FigureChip.displayName = 'FigureChip';
